import React, { useMemo } from "react";
import { NavLink, Route, Switch } from "react-router-dom";
import { Panel } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFieldsLabels } from "app/hooks";
import { canViewCompaniesField, canViewSkillsField } from "app/permissions";
import { t } from "app/utils";

import Locations from "./settings/Locations";
import Interests from "./settings/Interests";
import Departments from "./settings/Departments";
import OrganizationValues from "./settings/OrganizationValues";
import Skills from "./settings/Skills";
import Programs from "./settings/Programs";
import ClassYears from "./settings/ClassYears";
import Clubs from "./settings/Clubs";
import DegreesInfo from "./settings/DegreesInfo";
import Companies from "./settings/Companies";

const SettingOption = ({ to, phrase }) => (
  <li role="navigation">
    <NavLink to={to} activeClassName="active">{phrase}</NavLink>
  </li>
);

const Settings = () => {
  const { profile } = useProfile();
  const { organization } = profile;
  const { isEducational, name } = organization;
  const labels = useFieldsLabels();

  return (
    <Panel limitWidth>
      <ul role="tablist" aria-label={t("Settings options")} className="nav nav-tabs">

        {!isEducational && (
          <SettingOption to="/admin/departments" phrase={t(labels.departments.plural)} />
        )}
        {canViewCompaniesField(organization) && (
          <SettingOption to="/admin/companies" phrase={t(labels.companies.plural)} />
        )}
        <SettingOption to="/admin/locations" phrase={t("Locations")} />
        <SettingOption to="/admin/values" phrase={t("Values")} />
        <SettingOption to="/admin/interests" phrase={t("Hobbies/Interests")} />
        {!isEducational && canViewSkillsField(organization) && (
          <SettingOption to="/admin/skills" phrase={t("Skills")} />
        )}
        {isEducational && (
          <>
            <SettingOption to="/admin/programs" phrase={t("Programs")} />
            <SettingOption to="/admin/class-years" phrase={t(name === "Dartmouth Guarini" ? "Years in Program" : "Class Years")} />
            <SettingOption to="/admin/degrees" phrase={t("Degrees")} />
            <SettingOption to="/admin/clubs" phrase={t("Clubs")} />
          </>
        )}
      </ul>
      <Panel.Body>
        <Switch>
          {!isEducational && (
            <Route exact path="/admin/departments/:page?" component={Departments} />
          )}
          {canViewCompaniesField(organization) && (
            <Route exact path="/admin/companies/:page?" component={Companies} />
          )}
          <Route exact path="/admin/locations/:page?" component={Locations} />
          <Route exact path="/admin/values/:page?" component={OrganizationValues} />
          <Route exact path="/admin/interests/:page?" component={Interests} />
          {!isEducational && canViewSkillsField(organization) && (
            <Route exact path="/admin/skills/:page?" component={Skills} />
          )}
          {isEducational && (
            <>
              <Route exact path="/admin/programs/:page?" component={Programs} />
              <Route exact path="/admin/class-years/:page?" component={ClassYears} />
              <Route exact path="/admin/degrees/:page?" component={DegreesInfo} />
              <Route exact path="/admin/clubs/:page?" component={Clubs} />
            </>
          )}
        </Switch>
      </Panel.Body>
    </Panel>
  );
};

export default Settings;
